import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  DateInput,
  DateInputSize,
  InputSize,
  InputText,
  SelectSize,
} from '@ui/src';
import { Controller } from 'react-hook-form';
import AdditionalInfoBlock from '../additional-info-block';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';
import { SecondPassportTypes } from './types';

import cn from '../aml.module.css';

const SecondPassport = ({
  status,
  control,
  clearErrors,
  errors,
  countries,
  fields,
}: SecondPassportTypes) => {
  const [havingSecondPassport, setHavingSecondPassport] = useState(false);

  useEffect(() => {
    if (
      fields &&
      (fields?.secondPassportExpiryDate ||
        fields?.secondPassportIssueCountryId ||
        fields?.secondPassportIssueDate ||
        fields?.secondPassportNumber) &&
      !havingSecondPassport
    ) {
      setHavingSecondPassport(true);
    }
  }, [fields, havingSecondPassport]);

  return (
    <AdditionalInfoBlock title="2nd Passport">
      <div className={cn.addressCheckboxWrapper}>
        <Checkbox
          name="havingSecondPassport"
          type="checkbox"
          checked={havingSecondPassport}
          onChange={(e) => {
            setHavingSecondPassport(e.target.checked);
          }}
          title="Having a second passport"
          disabled={status === CustomerStatus.CLOSED}
          tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
          labelClassName={cn.addressCheckbox}
        />
      </div>
      {havingSecondPassport && (
        <>
          <RegulatedCompanyRow title="ID / Passport number">
            <Controller
              control={control}
              name="secondPassportNumber"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <InputText
                  name="secondPassportNumber"
                  placeholder="Enter first name..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['secondPassportNumber']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={errors?.secondPassportNumber?.message}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  disabled={status === CustomerStatus.CLOSED}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="ID / Passport issue country">
            <Controller
              control={control}
              name="secondPassportIssueCountryId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select country..."
                  list={countries}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['secondPassportIssueCountryId']);
                  }}
                  selectWrapperClassName={
                    status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                  }
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  disabled={status === CustomerStatus.CLOSED}
                  error={errors?.secondPassportIssueCountryId?.message}
                  fullWidth
                  borderless
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="ID / Passport issue date">
            <Controller
              control={control}
              name="secondPassportIssueDate"
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                let className;

                if (status === CustomerStatus.CLOSED) {
                  className = cn.dateInputDisabled;

                  if (value) {
                    className = `${cn.dateInputDisabled} ${
                      value ? cn.dateInputDisabledWithValue : ''
                    }`;
                  }
                }
                return (
                  <DateInput
                    value={value || ''}
                    placeholder="Enter date..."
                    onChange={(date) => {
                      onChange(date.toISOString());
                      clearErrors(['secondPassportIssueDate']);
                    }}
                    className={className}
                    size={DateInputSize.SMALL}
                    disabledAfter={new Date()}
                    error={errors?.secondPassportIssueDate?.message}
                    tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                    disabled={status === CustomerStatus.CLOSED}
                    borderless
                    withoutCustomSelector
                  />
                );
              }}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="ID / Passport expiry date">
            <Controller
              control={control}
              name="secondPassportExpiryDate"
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                let className;

                if (status === CustomerStatus.CLOSED) {
                  className = cn.dateInputDisabled;

                  if (value) {
                    className = `${cn.dateInputDisabled} ${
                      value ? cn.dateInputDisabledWithValue : ''
                    }`;
                  }
                }

                return (
                  <DateInput
                    value={value || ''}
                    placeholder="Enter date..."
                    onChange={(date) => {
                      onChange(date.toISOString());
                      clearErrors(['secondPassportExpiryDate']);
                    }}
                    className={className}
                    size={DateInputSize.SMALL}
                    error={errors?.secondPassportExpiryDate?.message}
                    tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                    disabled={status === CustomerStatus.CLOSED}
                    borderless
                    withoutCustomSelector
                  />
                );
              }}
            />
          </RegulatedCompanyRow>
        </>
      )}
    </AdditionalInfoBlock>
  );
};

export default SecondPassport;
